import type { LearnTopic } from "../topic-types";
import { IFS_TOPIC } from "./ifs";
import { ATTACHMENT_TOPIC } from "./attachment";
import { CPTSD_TOPIC } from "./cptsd";
import { PTSD_TOPIC } from "./ptsd";
import { TRAUMA_RESPONSES_TOPIC } from "./trauma-responses";
import { EMOTIONAL_FLASHBACKS_TOPIC } from "./emotional-flashbacks";
import { DISSOCIATION_TOPIC } from "./dissociation";
import { TRIGGERS_TOPIC } from "./triggers";
import { TRAUMA_BONDS_TOPIC } from "./trauma-bonds";
import { BOUNDARIES_TOPIC } from "./boundaries";
import { PEOPLE_PLEASING_TOPIC } from "./people-pleasing";
import { INNER_CHILD_TOPIC } from "./inner-child";
import { TRAUMA_RELATIONSHIPS_TOPIC } from "./trauma-relationships";
import { INNER_CRITIC_TOPIC } from "./inner-critic";
import { GRIEF_TRAUMA_TOPIC } from "./grief-trauma";
import { TRAUMA_SLEEP_TOPIC } from "./trauma-sleep";
import { ABUSIVE_RELATIONSHIPS_TOPIC } from "./abusive-relationships";
import { HYPERVIGILANCE_TOPIC } from "./hypervigilance";
import { GROUNDING_TECHNIQUES_TOPIC } from "./grounding-techniques";
import { AUTISM_TRAUMA_TOPIC } from "./autism-trauma";
import {
  CHILDHOOD_DEVELOPMENTAL_TRAUMA_TOPIC,
  EMOTIONAL_NEGLECT_TOPIC,
  POST_TRAUMATIC_GROWTH_TOPIC,
  TRAUMA_BODY_TOPIC,
  TRAUMA_NERVOUS_SYSTEM_TOPIC,
} from "./trauma-foundations";
import {
  COERCIVE_CONTROL_TOPIC,
  EMOTIONAL_ABUSE_TOPIC,
  NARCISSISTIC_ABUSE_TOPIC,
  SEXUAL_TRAUMA_TOPIC,
  TRAUMA_INTIMACY_SEX_TOPIC,
} from "./relationships-safety";
import {
  ADHD_TRAUMA_TOPIC,
  ANXIETY_TRAUMA_TOPIC,
  OCD_TRAUMA_TOPIC,
  SELF_WORTH_TOPIC,
  TRAUMA_ANGER_TOPIC,
} from "./mental-health-recovery";

/**
 * Registry of structured Learn topics.
 * Add a topic here and it becomes routable, searchable and expandable
 * without touching any page component.
 */
export const LEARN_TOPICS: Record<string, LearnTopic> = {
  [IFS_TOPIC.slug]: IFS_TOPIC,
  [ATTACHMENT_TOPIC.slug]: ATTACHMENT_TOPIC,
  [CPTSD_TOPIC.slug]: CPTSD_TOPIC,
  [PTSD_TOPIC.slug]: PTSD_TOPIC,
  [TRAUMA_RESPONSES_TOPIC.slug]: TRAUMA_RESPONSES_TOPIC,
  [EMOTIONAL_FLASHBACKS_TOPIC.slug]: EMOTIONAL_FLASHBACKS_TOPIC,
  [DISSOCIATION_TOPIC.slug]: DISSOCIATION_TOPIC,
  [TRIGGERS_TOPIC.slug]: TRIGGERS_TOPIC,
  [TRAUMA_BONDS_TOPIC.slug]: TRAUMA_BONDS_TOPIC,
  [BOUNDARIES_TOPIC.slug]: BOUNDARIES_TOPIC,
  [PEOPLE_PLEASING_TOPIC.slug]: PEOPLE_PLEASING_TOPIC,
  [INNER_CHILD_TOPIC.slug]: INNER_CHILD_TOPIC,
  [TRAUMA_RELATIONSHIPS_TOPIC.slug]: TRAUMA_RELATIONSHIPS_TOPIC,
  [INNER_CRITIC_TOPIC.slug]: INNER_CRITIC_TOPIC,
  [GRIEF_TRAUMA_TOPIC.slug]: GRIEF_TRAUMA_TOPIC,
  [TRAUMA_SLEEP_TOPIC.slug]: TRAUMA_SLEEP_TOPIC,
  [ABUSIVE_RELATIONSHIPS_TOPIC.slug]: ABUSIVE_RELATIONSHIPS_TOPIC,
  [HYPERVIGILANCE_TOPIC.slug]: HYPERVIGILANCE_TOPIC,
  [GROUNDING_TECHNIQUES_TOPIC.slug]: GROUNDING_TECHNIQUES_TOPIC,
  [AUTISM_TRAUMA_TOPIC.slug]: AUTISM_TRAUMA_TOPIC,
  [CHILDHOOD_DEVELOPMENTAL_TRAUMA_TOPIC.slug]: CHILDHOOD_DEVELOPMENTAL_TRAUMA_TOPIC,
  [EMOTIONAL_NEGLECT_TOPIC.slug]: EMOTIONAL_NEGLECT_TOPIC,
  [EMOTIONAL_ABUSE_TOPIC.slug]: EMOTIONAL_ABUSE_TOPIC,
  [TRAUMA_NERVOUS_SYSTEM_TOPIC.slug]: TRAUMA_NERVOUS_SYSTEM_TOPIC,
  [ANXIETY_TRAUMA_TOPIC.slug]: ANXIETY_TRAUMA_TOPIC,
  [SEXUAL_TRAUMA_TOPIC.slug]: SEXUAL_TRAUMA_TOPIC,
  [COERCIVE_CONTROL_TOPIC.slug]: COERCIVE_CONTROL_TOPIC,
  [NARCISSISTIC_ABUSE_TOPIC.slug]: NARCISSISTIC_ABUSE_TOPIC,
  [TRAUMA_ANGER_TOPIC.slug]: TRAUMA_ANGER_TOPIC,
  [TRAUMA_BODY_TOPIC.slug]: TRAUMA_BODY_TOPIC,
  [TRAUMA_INTIMACY_SEX_TOPIC.slug]: TRAUMA_INTIMACY_SEX_TOPIC,
  [SELF_WORTH_TOPIC.slug]: SELF_WORTH_TOPIC,
  [ADHD_TRAUMA_TOPIC.slug]: ADHD_TRAUMA_TOPIC,
  [OCD_TRAUMA_TOPIC.slug]: OCD_TRAUMA_TOPIC,
  [POST_TRAUMATIC_GROWTH_TOPIC.slug]: POST_TRAUMATIC_GROWTH_TOPIC,
};

/** Legacy / alternate slugs that should resolve to an existing topic. */
export const LEARN_TOPIC_ALIASES: Record<string, string> = {
  "trauma-bonding": TRAUMA_BONDS_TOPIC.slug,
  grounding: GROUNDING_TECHNIQUES_TOPIC.slug,
  "grounding-technique": GROUNDING_TECHNIQUES_TOPIC.slug,
  autism: AUTISM_TRAUMA_TOPIC.slug,
  "autism-and-trauma": AUTISM_TRAUMA_TOPIC.slug,
};

export function resolveTopicSlug(slug: string): string {
  return LEARN_TOPIC_ALIASES[slug] ?? slug;
}

export function getTopic(slug: string): LearnTopic | null {
  return LEARN_TOPICS[resolveTopicSlug(slug)] ?? null;
}


export {
  IFS_TOPIC,
  ATTACHMENT_TOPIC,
  CPTSD_TOPIC,
  PTSD_TOPIC,
  TRAUMA_RESPONSES_TOPIC,
  EMOTIONAL_FLASHBACKS_TOPIC,
  DISSOCIATION_TOPIC,
  TRIGGERS_TOPIC,
  TRAUMA_BONDS_TOPIC,
  BOUNDARIES_TOPIC,
  PEOPLE_PLEASING_TOPIC,
  INNER_CHILD_TOPIC,
  TRAUMA_RELATIONSHIPS_TOPIC,
  INNER_CRITIC_TOPIC,
  GRIEF_TRAUMA_TOPIC,
  TRAUMA_SLEEP_TOPIC,
  ABUSIVE_RELATIONSHIPS_TOPIC,
  HYPERVIGILANCE_TOPIC,
  AUTISM_TRAUMA_TOPIC,
  GROUNDING_TECHNIQUES_TOPIC,
  CHILDHOOD_DEVELOPMENTAL_TRAUMA_TOPIC,
  EMOTIONAL_NEGLECT_TOPIC,
  EMOTIONAL_ABUSE_TOPIC,
  TRAUMA_NERVOUS_SYSTEM_TOPIC,
  ANXIETY_TRAUMA_TOPIC,
  SEXUAL_TRAUMA_TOPIC,
  COERCIVE_CONTROL_TOPIC,
  NARCISSISTIC_ABUSE_TOPIC,
  TRAUMA_ANGER_TOPIC,
  TRAUMA_BODY_TOPIC,
  TRAUMA_INTIMACY_SEX_TOPIC,
  SELF_WORTH_TOPIC,
  ADHD_TRAUMA_TOPIC,
  OCD_TRAUMA_TOPIC,
  POST_TRAUMATIC_GROWTH_TOPIC,
};
